<script lang="ts">
	import { browser } from '$app/environment';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import Attribution from '$lib/components/ui/Attribution.svelte';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { UrlUtil } from '$lib/utils/url.util';
	import { watch } from 'runed';
	import { onDestroy, onMount, type Component } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import { fade } from 'svelte/transition';
	import IconChevronLeft from '~icons/mdi/chevron-left';
	import IconChevronRight from '~icons/mdi/chevron-right';
	import IconClose from '~icons/mdi/close';
	import IconImage from '~icons/mdi/image';
	import IconMapMarkerRadius from '~icons/mdi/map-marker-radius';

	const {
		images,
		startIndex,
		fadingDuration = 200,
		onClose
	}: { images: ImageDetails[]; startIndex: number; fadingDuration?: number; onClose: () => void } = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();
	const gesture = {
		isMultiTouch: false,
		lastTap: 0,
		isDragging: false,
		startX: 0,
		startY: 0,
		scrollLeft: 0,
		scrollTop: 0,
		touchStartX: 0,
		touchEndX: 0,
		touchStartY: 0,
		touchEndY: 0,
		lastWheelTime: 0
	};

	let containerNode = $state<HTMLElement | undefined>(undefined);
	let imageLoaded = $state<boolean>(true);
	let doubleTapZoomed = $state<boolean>(false);
	let isFlipped = $state<boolean>(false);
	let thumbnailContainer = $state<HTMLElement | null>(null);
	let maxThumbnailScrollPercentage = $state<number>(0);

	let activeIndex = $derived<number>(startIndex);
	let highestIndexReached = $derived<number>(startIndex);
	let displayedImage = $derived<ImageDetails>(images[startIndex]);
	const activeImage = $derived<ImageDetails>(images[activeIndex]);

	useBackButtonInterception({
		stateKey: 'galleryOpen',
		enabled: () => deviceScreen.smallerThanDesktop,
		onBack: () => onClose(),
		onForward: 'skip'
	});

	onMount(() => {
		// add padding to body to account for scrollbar, otherwise the body will expand/contract when the scrollbar appears/disappears when the modal is opened/closed
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.overflow = 'hidden';
		document.body.style.paddingRight = `${scrollbarWidth}px`;

		// improve performance
		containerNode?.addEventListener('wheel', handleWheel, { passive: true });
		analyticsApi.sendAnalytics({
			event: ANALYTICS_EVENT_NAMES.imageOpened,
			payload: { index: startIndex, filename: images[startIndex].filename }
		});
	});

	onDestroy(() => {
		document.body.style.overflow = '';
		document.body.style.paddingRight = '';

		containerNode?.removeEventListener('wheel', handleWheel);

		const totalImages = images.length;
		const photosViewedPercent = totalImages > 0 ? Math.round(((highestIndexReached + 1) / totalImages) * 100) : 0;

		analyticsApi.sendAnalytics(
			{
				event: ANALYTICS_EVENT_NAMES.imageClosed,
				payload: {
					photosViewedPercent,
					thumbsScrolledPercent: maxThumbnailScrollPercentage,
					images: images.length
				}
			},
			{ keepalive: true }
		);
	});

	watch(
		() => activeIndex,
		() => {
			if (activeIndex > highestIndexReached) {
				highestIndexReached = activeIndex;
			}
			isFlipped = false;
			imageLoaded = false; // Trigger smooth fade-out
			doubleTapZoomed = false;

			// Swap the layout dimensions and image source only once the old image is invisible
			setTimeout(() => {
				displayedImage = activeImage;
			}, 150);

			if (thumbnailContainer) {
				const activeEl = thumbnailContainer.querySelector('[data-active="true"]') as HTMLElement;
				if (activeEl) {
					activeEl.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'center'
					});
				}
			}
		}
	);

	function handleThumbnailScroll(e: Event): void {
		const target = e.currentTarget as HTMLElement;
		const maxScroll = target.scrollWidth - target.clientWidth;
		if (maxScroll > 0) {
			const pct = Math.min(100, Math.max(0, Math.round((target.scrollLeft / maxScroll) * 100)));
			if (pct > maxThumbnailScrollPercentage) {
				maxThumbnailScrollPercentage = pct;
			}
		}
	}

	function handleTouchStart(e: TouchEvent): void {
		// If the user has more than one finger on screen, or is already zoomed in, ignore swipe
		if (e.touches.length > 1 || isZoomed()) {
			gesture.isMultiTouch = true;
			return;
		}
		gesture.isMultiTouch = false;
		gesture.touchStartX = e.changedTouches[0].clientX;
		gesture.touchStartY = e.changedTouches[0].clientY;
	}

	function handleTouchMove(e: TouchEvent): void {
		// Flag if a second finger was added during the gesture
		if (e.touches.length > 1) {
			gesture.isMultiTouch = true;
		}
	}

	function handleTouchEnd(e: TouchEvent): void {
		// Prevent image switching or modal closure if we are zooming or panning
		if (gesture.isMultiTouch || isZoomed()) {
			gesture.isMultiTouch = false;
			return;
		}
		gesture.touchEndX = e.changedTouches[0].clientX;
		gesture.touchEndY = e.changedTouches[0].clientY;
		handleSwipe();
	}

	function handleSwipe(): void {
		const minSwipeDistance = 50;
		const diffX = gesture.touchStartX - gesture.touchEndX;
		const diffY = gesture.touchStartY - gesture.touchEndY;

		const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX);
		if (isVerticalSwipe) {
			if (diffY < -minSwipeDistance) {
				onClose();
			}
		} else if (Math.abs(diffX) > minSwipeDistance) {
			if (diffX > 0) {
				activeIndex = activeIndex < images.length - 1 ? activeIndex + 1 : 0;
			} else {
				activeIndex = activeIndex > 0 ? activeIndex - 1 : images.length - 1;
			}
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') onClose();
		if (e.key === 'ArrowRight') activeIndex = (activeIndex + 1) % images.length;
		if (e.key === 'ArrowLeft') activeIndex = (activeIndex - 1 + images.length) % images.length;
	}

	function handleWheel(e: WheelEvent) {
		const now = Date.now();
		const isScrollingCooldown = now - gesture.lastWheelTime < 300;
		if (isScrollingCooldown) return;

		const wasTinyScroll = Math.abs(e.deltaY) < 10;
		if (wasTinyScroll) return;

		const isScrollingDown = e.deltaY > 0;
		if (isScrollingDown) {
			const nextImageIndex = activeIndex < images.length - 1 ? activeIndex + 1 : 0;
			activeIndex = nextImageIndex;
		} else {
			const previousImageIndex = activeIndex > 0 ? activeIndex - 1 : images.length - 1;
			activeIndex = previousImageIndex;
		}
		gesture.lastWheelTime = now;
	}

	function handlePhotoClick(e: MouseEvent) {
		const now = Date.now();
		const DOUBLE_PRESS_DELAY = 300;
		if (now - gesture.lastTap < DOUBLE_PRESS_DELAY) {
			e.preventDefault();
			e.stopPropagation();
			doubleTapZoomed = !doubleTapZoomed;
		} else {
			gesture.lastTap = now;
		}
	}

	function handleMouseDown(e: MouseEvent) {
		if (!doubleTapZoomed) return;
		const container = e.currentTarget as HTMLElement;
		gesture.isDragging = true;
		container.style.cursor = 'grabbing';
		gesture.startX = e.pageX - container.offsetLeft;
		gesture.startY = e.pageY - container.offsetTop;
		gesture.scrollLeft = container.scrollLeft;
		gesture.scrollTop = container.scrollTop;
	}

	function handleMouseMove(e: MouseEvent) {
		if (!gesture.isDragging || !doubleTapZoomed) return;
		e.preventDefault();
		const container = e.currentTarget as HTMLElement;
		const x = e.pageX - container.offsetLeft;
		const y = e.pageY - container.offsetTop;
		const walkX = (x - gesture.startX) * 1.5; // Drag speed multiplier
		const walkY = (y - gesture.startY) * 1.5;
		container.scrollLeft = gesture.scrollLeft - walkX;
		container.scrollTop = gesture.scrollTop - walkY;
	}

	function handleMouseUpOrLeave(e: MouseEvent) {
		gesture.isDragging = false;
		const container = e.currentTarget as HTMLElement;
		if (doubleTapZoomed) {
			container.style.cursor = 'grab';
		} else {
			container.style.cursor = '';
		}
	}

	// helps us trap focus within the modal, otherwise pressing tab will move focus to the next element on the page itself, leaving the modal
	function focusTrap(node: HTMLElement) {
		const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

		const firstFocusable = node.querySelector(focusableElements) as HTMLElement;
		const focusableContent = node.querySelectorAll(focusableElements);
		const lastFocusable = focusableContent[focusableContent.length - 1] as HTMLElement;

		// Auto-focus the first element (Close button) when opened
		if (firstFocusable) firstFocusable.focus();

		const handleTrap = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				if (e.shiftKey) {
					// Shift + Tab
					if (document.activeElement === firstFocusable) {
						e.preventDefault();
						lastFocusable.focus();
					}
				} else {
					// Tab
					if (document.activeElement === lastFocusable) {
						e.preventDefault();
						firstFocusable.focus();
					}
				}
			}
		};
		node.addEventListener('keydown', handleTrap);
		return () => {
			node.removeEventListener('keydown', handleTrap);
		};
	}

	function isZoomed(): boolean {
		if (doubleTapZoomed) return true;
		return browser && window.visualViewport ? window.visualViewport.scale > 1 : false;
	}
</script>

<svelte:window onkeydown={handleKeydown} />

<div
	bind:this={containerNode}
	{@attach focusTrap}
	class="fixed inset-0 z-100 flex touch-auto flex-col items-center justify-center bg-black/95 outline-none"
	transition:fade={{ duration: fadingDuration }}
	role="dialog"
	aria-modal="true"
	aria-label="Image Gallery"
	onclick={onClose}
	tabindex="-1"
	onkeydown={(e) => {
		if (e.key === 'Escape') onClose();
	}}
	ontouchstart={handleTouchStart}
	ontouchmove={handleTouchMove}
	ontouchend={handleTouchEnd}
>
	{@render CloseBtn()}

	<div class="pointer-events-none relative z-10 flex h-full w-full flex-1 items-center justify-center overflow-hidden p-4">
		{@render NavButton(IconChevronLeft, 'Previous image', 'left-4', (e) => {
			e.stopPropagation();
			activeIndex = activeIndex > 0 ? activeIndex - 1 : images.length - 1;
		})}

		{@render ActiveImage()}

		{@render NavButton(IconChevronRight, 'Next image', 'right-4', (e) => {
			e.stopPropagation();
			activeIndex = activeIndex < images.length - 1 ? activeIndex + 1 : 0;
		})}
	</div>

	<div
		class="pointer-events-auto z-50 w-full shrink-0 touch-pan-x px-3 pb-4 lg:pb-2"
		role="toolbar"
		aria-label="Thumbnails"
		onclick={(e) => e.stopPropagation()}
		onkeydown={() => {}}
		tabindex="0"
		ontouchstart={(e) => e.stopPropagation()}
		ontouchend={(e) => e.stopPropagation()}
	>
		{@render ThumbnailStrip()}
	</div>
</div>

{#snippet CloseBtn()}
	<button
		class="btn pointer-events-auto absolute top-4 right-4 z-50 btn-circle btn-ghost text-white hover:bg-white/20"
		onclick={(e) => {
			e.stopPropagation();
			onClose();
		}}
		aria-label="Close Gallery"
	>
		<IconClose class="text-3xl" />
	</button>
{/snippet}

{#snippet ActiveImage()}
	<div
		class={{
			'pointer-events-auto relative rounded-sm': true,
			'touch-pan-x touch-pan-y overflow-auto': doubleTapZoomed,
			'overflow-hidden': !doubleTapZoomed,
			'cursor-grab': doubleTapZoomed,
			'max-h-[85vh] max-w-full': true
		}}
		role="presentation"
		onclick={(e) => e.stopPropagation()}
		onkeydown={(e) => e.stopPropagation()}
		onmousedown={handleMouseDown}
		onmousemove={handleMouseMove}
		onmouseup={handleMouseUpOrLeave}
		onmouseleave={handleMouseUpOrLeave}
	>
		<div
			class={{
				'relative flex h-full w-full': true,
				'items-start justify-start': doubleTapZoomed,
				'items-center justify-center': !doubleTapZoomed
			}}
		>
			<div class="flex h-full w-full items-center justify-center" class:pointer-events-none={isFlipped}>
				{@render PhotoView()}
			</div>

			{#if isFlipped && activeImage.latitude && activeImage.longitude}
				<div
					class="absolute inset-0 z-20 flex h-full w-full flex-col overflow-hidden rounded-sm bg-base-200"
					transition:fade={{ duration: 250 }}
				>
					{@render MapView({ latitude: activeImage.latitude, longitude: activeImage.longitude })}
				</div>
			{/if}
		</div>
	</div>

	{#snippet PhotoView()}
		<button type="button" class="contents" onclick={handlePhotoClick} aria-label="Zoom Image">
			<!-- don't add height/width here, it'll break clicking the background to close the modal -->
			<img
				class={{
					'rounded-sm object-contain shadow-2xl transition-opacity duration-300 ease-out outline-none select-none': true,
					'opacity-0': !imageLoaded,
					'max-h-[85vh] max-w-full': !doubleTapZoomed,
					'z-40 h-auto max-h-none w-[250%] max-w-none': doubleTapZoomed
				}}
				style="aspect-ratio: {displayedImage.width} / {displayedImage.height}"
				src={UrlUtil.getFullImageUrl(displayedImage)}
				alt={displayedImage.altText}
				draggable="false"
				onload={() => (imageLoaded = true)}
			/>
		</button>

		{#if !isFlipped}
			<!-- fade to prevent flashing when switching images -->
			<div class="absolute bottom-3 left-3 z-30 transition-opacity duration-300" class:opacity-0={!imageLoaded}>
				<Attribution
					attribution={{
						author: activeImage.authorUsername,
						sourceUrl: `https://commons.wikimedia.org/?curid=${activeImage.wikiId}`,
						licence: activeImage.licence
					}}
				/>
			</div>

			{#if activeImage.latitude && activeImage.longitude}
				<div
					class="tooltip absolute tooltip-left right-3 bottom-3 z-30 transition-opacity duration-300"
					class:opacity-0={!imageLoaded}
					data-tip="To Map"
				>
					{@render FlipToMapBtn()}
				</div>
			{/if}
		{/if}

		{#snippet FlipToMapBtn()}
			<button
				class="btn btn-circle border-white/20 shadow-lg btn-primary btn-sm"
				aria-label="View Location on Map"
				onclick={(e) => {
					e.stopPropagation();
					doubleTapZoomed = false;
					isFlipped = true;
				}}
			>
				<IconMapMarkerRadius class="text-lg" />
			</button>
		{/snippet}
	{/snippet}

	{#snippet MapView({ latitude, longitude }: { latitude: number; longitude: number })}
		<div class="relative h-full w-full flex-1">
			{#if browser}
				{#await import('$lib/components/ui/map/SinglePointMap.svelte') then { default: SinglePointMap }}
					<SinglePointMap primaryPoint={{ latitude, longitude }} />
				{/await}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
					<span class="loading mr-2 loading-spinner text-primary"></span>
					<span>Loading map of this photo...</span>
				</div>
			{/if}
		</div>

		<div class="tooltip absolute tooltip-left right-3 bottom-3 z-30" data-tip="To Photo">
			{@render FlipToPhotoBtn()}
		</div>

		{#snippet FlipToPhotoBtn()}
			<button
				class="btn btn-circle border-white/20 shadow-lg btn-neutral btn-sm"
				aria-label="Back to Photo"
				onclick={(e) => {
					e.stopPropagation();
					isFlipped = false;
				}}
			>
				<IconImage class="text-lg" />
			</button>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet NavButton(Icon: Component, ariaLabel: string, sideClass: ClassValue, onClick: (e: MouseEvent) => void)}
	<button
		class="btn pointer-events-auto absolute top-1/2 border-0 btn-outline btn-primary {sideClass} z-50 hidden btn-circle -translate-y-1/2 md:flex"
		onclick={onClick}
		aria-label={ariaLabel}
	>
		<Icon class="text-3xl" />
	</button>
{/snippet}

{#snippet ThumbnailStrip()}
	<!-- limit the width and don't add justify-center to the child div, so the first thumbnails are always visible, especially on mobile -->
	<div class="flex justify-center">
		<div
			bind:this={thumbnailContainer}
			onscroll={handleThumbnailScroll}
			class="scrollbar-hide flex w-full gap-3 overflow-x-auto py-2 md:max-w-[50%]"
		>
			{#each images as img, i (img.filename)}
				<button
					class={{
						'h-16 w-16 shrink-0 overflow-hidden rounded-field border-2 transition-all': true,
						'scale-110 border-white opacity-100 shadow-lg': i === activeIndex,
						'border-transparent opacity-50 hover:opacity-100': i !== activeIndex
					}}
					onclick={() => (activeIndex = i)}
					aria-label="View image {i + 1}"
					aria-current={i === activeIndex ? 'true' : undefined}
					data-active={i === activeIndex ? 'true' : undefined}
				>
					<img
						class="h-full w-full object-cover"
						src={UrlUtil.getFullImageUrl(img)}
						alt={img.altText}
						height={img.height}
						width={img.width}
						draggable="false"
						loading={i <= 6 ? 'eager' : 'lazy'}
					/>
				</button>
			{/each}
		</div>
	</div>
{/snippet}
